import React, { useEffect, useMemo, useState } from "react";
import {
  PageAction,
  PageTemplate,
} from "@components/pageTemplate/PageTemplate";
import { getRoute, Routes } from "~/routes";
import {
  SoilSample,
} from "generated/graphql";
import { LoadingState } from "@util/loadingstate.util";
import { useInjection } from "@di/DependencyContext";
import { SoilSampleService, SoilSampleSortKey } from "@modules/soil-sample/soil-sample.service";
import { DependencyType } from "@di/DependencyType";
import { PartialDeep } from "type-fest";
import { parseServerError } from "@util/parseError";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { compact } from "@util/array.util";
import { useDebouncedCallback } from "use-debounce";
import { cn } from "~/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";
import { Button } from "~/components/ui/button";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { Label } from "~/components/ui/label";

export interface SoilSamplesListProps {}

export const SoilSamplesList: React.FC<SoilSamplesListProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.Loading
  );
  const [samples, setSamples] = useState<PartialDeep<SoilSample>[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const soilSampleService = useInjection<SoilSampleService>(
    DependencyType.SoilSampleService
  );
  const [page, setPage] = useState(
    parseInt(queryParams.get("page") ?? "1") - 1
  );
  const [query, setQuery] = useState(queryParams.get("query") || "");
  const [sortKey, setSortKey] = useState<SoilSampleSortKey>(
    (queryParams.get("sortKey") as SoilSampleSortKey) || "name"
  );
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">(
    (queryParams.get("sortDirection") as "ASC" | "DESC") || "DESC"
  );
  const pageSize = 10;
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageCursors, setPageCursors] = useState<Map<number, string | null>>(new Map([[0, null]]));

  const updateResults = useDebouncedCallback(async () => {
    const cursor = pageCursors.get(page);
    setLoadingState(LoadingState.Loading);
    try {
      const results = await soilSampleService.findMany(
        cursor ?? undefined,
        query === "" ? undefined : query,
        sortKey,
        sortDirection
      );
      if (results?.nodes) {
        setSamples(compact(results.nodes));
      }
      setHasNextPage(results?.hasNextPage ?? false);
      // Store cursor for next page
      if (results?.endCursor) {
        setPageCursors(prev => new Map(prev).set(page + 1, results.endCursor ?? null));
      }
      setLoadingState(LoadingState.Loaded);
    } catch (e) {
      const parsed = parseServerError(e);
      toast.error(parsed.message);
      setLoadingState(LoadingState.Error);
    }
  }, 500);

  useEffect(() => {
    void updateResults();
  }, [page, query, sortKey, sortDirection]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", (page + 1).toString());
    if (query) {
      params.set("query", query);
    }
    if (sortKey) {
      params.set("sortKey", sortKey);
    }
    if (sortDirection) {
      params.set("sortDirection", sortDirection);
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [page, query, sortKey, sortDirection]);

  const updateQuery = (val: string) => {
    setQuery(val);
    setPage(0);
    setPageCursors(new Map([[0, null]]));
  };

  const handleSort = (key: SoilSampleSortKey) => {
    const nextDirection: "ASC" | "DESC" =
      sortKey === key && sortDirection === "ASC" ? "DESC" : "ASC";
    setSortKey(key);
    setSortDirection(nextDirection);
    setPage(0);
    setPageCursors(new Map([[0, null]]));
  };

  const columns = useMemo<
    Array<{
      label: string;
      key: string;
      sortKey?: SoilSampleSortKey;
      render: (sample: PartialDeep<SoilSample>) => React.ReactNode;
    }>
  >(
    () => [
      {
        label: "Name",
        key: "name",
        sortKey: "name",
        render: (sample: PartialDeep<SoilSample>) => (
          <span className="text-sm font-semibold text-primary">
            {sample.name ?? "—"}
          </span>
        ),
      },
      {
        label: "Created",
        key: "createdAt",
        sortKey: "createdAt",
        render: (sample: PartialDeep<SoilSample>) => (
          <span className="text-sm font-semibold">
            {sample.createdAt
              ? new Date(sample.createdAt).toLocaleString()
              : "—"}
          </span>
        ),
      },
      {
        label: "Last Updated",
        key: "updatedAt",
        sortKey: "updatedAt",
        render: (sample: PartialDeep<SoilSample>) => (
          <span className="text-sm font-semibold">
            {sample.updatedAt
              ? new Date(sample.updatedAt).toLocaleString()
              : "—"}
          </span>
        ),
      },
      {
        label: "Lab results",
        key: "labResults",
        render: (sample: PartialDeep<SoilSample>) => (
          <span className="text-sm font-semibold">
            {sample.labResults?.nodes?.length ?? 0}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <PageTemplate
      title="Soil Samples"
      activeItem="Soil Samples"
      activeSubItem="List"
      breadcrumbs={[
        { label: "Home", path: Routes.Home },
        { label: "Soil Samples", path: Routes.SoilSamples },
      ]}
      actions={[
        {
          text: "Create new sample",
          disabled: loadingState === LoadingState.Loading,
          action: () => navigate(getRoute(Routes.SoilSampleCreate)),
          variant: "primary",
        } as PageAction,
      ]}
    >
      <Card className="gap-1">
        <CardHeader>
          <CardTitle className="text-base">Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex max-w-sm flex-col gap-2">
            <Label htmlFor="soil-sample-search">Sample name</Label>
            <Input
              id="soil-sample-search"
              value={query}
              onChange={(event) => updateQuery(event.target.value)}
              placeholder="Search samples"
            />
          </div>
        </CardContent>
      </Card>
      <Card className="py-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column.key}>
                    {column.sortKey ? (
                      <Button
                        type="button"
                        variant="ghost"
                        className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                        onClick={() => handleSort(column.sortKey!)}
                      >
                        {column.label}
                        {sortKey === column.sortKey ? (
                          sortDirection === "ASC" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    ) : (
                      <span className="text-sm font-semibold">
                        {column.label}
                      </span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingState === LoadingState.Loading ? (
                Array.from({ length: Math.min(pageSize, 5) }).map(
                  (_, index) => (
                    <TableRow key={`loading-${index}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={`${column.key}-loading-${index}`}
                          className="h-12"
                        >
                          <Skeleton className="h-4 w-2/3" />
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                )
              ) : loadingState === LoadingState.Error ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-sm text-destructive"
                  >
                    Unable to load soil samples. Please try again shortly.
                  </TableCell>
                </TableRow>
              ) : samples.length > 0 ? (
                samples.map((sample, index) => (
                  <TableRow
                    key={sample.id ?? `sample-${index}`}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(
                        getRoute(Routes.SoilSampleDetails, { id: sample.id! })
                      )
                    }
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={`${sample.id ?? index}-${column.key}`}
                        className="h-12"
                      >
                        {column.render(sample)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-sm text-muted-foreground"
                  >
                    No soil samples found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {(page > 0 || hasNextPage) && (
        <Card>
          <CardContent>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      if (page > 0) {
                        setPage(page - 1);
                      }
                    }}
                    className={cn(
                      page === 0 && "pointer-events-none opacity-50"
                    )}
                    aria-disabled={page === 0}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-4 text-sm">Page {page + 1}</span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      if (hasNextPage) {
                        setPage(page + 1);
                      }
                    }}
                    className={cn(
                      !hasNextPage && "pointer-events-none opacity-50"
                    )}
                    aria-disabled={!hasNextPage}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardContent>
        </Card>
      )}
    </PageTemplate>
  );
};
